'use client';

import { type DeepResearchHistoryItem } from '@/app/services/deep-research';
import PublishSquareButton from '@/components/publish-square-button';
import ShareButton from '@/components/share-button';
import {
  fetchSidebarDeepResearchHistory,
  patchSidebarDeepResearchHistory,
  sidebarHistoryKeys,
} from '@/components/sidebar-history/sidebar-history-cache';
import { SmartLink } from '@/components/smart-link';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircleIcon,
  MoreVerticalIcon,
  SendIcon,
  Share2Icon,
} from 'lucide-react';

interface DeepResearchSidebarHistoryListProps {
  currentDeepResearchId: string | null;
  isDeepResearchRoute: boolean;
  isAuthenticated: boolean;
}

export function DeepResearchSidebarHistoryList({
  currentDeepResearchId,
  isDeepResearchRoute,
  isAuthenticated,
}: DeepResearchSidebarHistoryListProps) {
  const queryClient = useQueryClient();
  const { data: items, isLoading } = useQuery({
    queryKey: sidebarHistoryKeys.deepResearch,
    queryFn: fetchSidebarDeepResearchHistory,
    staleTime: 60 * 1000,
    refetchOnMount: 'always',
  });

  if (isLoading) {
    return Array.from({ length: 8 }).map((_, index) => (
      <div key={index} className="px-2 py-1">
        <Skeleton className="h-4 w-full rounded-md" />
      </div>
    ));
  }

  if (!items || items.length === 0) {
    return (
      <div className="px-2 py-1.5 text-sm text-muted-foreground">
        暂无研究记录
      </div>
    );
  }

  return items.map((item: DeepResearchHistoryItem) => {
    const active = isDeepResearchRoute && currentDeepResearchId === item.drid;
    const canShare = item.status === 'DONE';

    return (
      <div
        key={item.drid}
        className={`group/item relative rounded-lg ${
          active ? 'bg-sidebar-accent' : 'hover:bg-sidebar-accent'
        }`}
      >
        <SmartLink
          href={`/deep-research/${item.drid}`}
          authenticated={isAuthenticated}
          mode="intent"
          className="flex items-center px-2 py-1.5 text-sm text-muted-foreground transition-colors hover:text-sidebar-accent-foreground"
        >
          <span className="line-clamp-1 break-all">
            {item.user_query || '无标题'}
          </span>
        </SmartLink>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon-sm"
              className={`absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2 rounded-full opacity-0 transition-opacity group-hover/item:opacity-100 data-[state=open]:opacity-100 ${
                active
                  ? 'bg-sidebar-accent text-sidebar-accent-foreground hover:bg-sidebar-accent/80'
                  : 'text-muted-foreground group-hover/item:bg-sidebar-accent group-hover/item:text-sidebar-accent-foreground hover:bg-sidebar-accent/80 hover:text-sidebar-accent-foreground data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground'
              }`}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
            >
              <MoreVerticalIcon className="h-3.5 w-3.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="w-36 rounded-2xl p-2 shadow-sm"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
            }}
          >
            <DropdownMenuItem
              className="rounded-xl"
              disabled={!canShare}
              asChild
              onSelect={(event) => {
                event.preventDefault();
              }}
            >
              <div>
                <ShareButton
                  disabled={!canShare}
                  sid={item.drid}
                  variant="ghost"
                  size="sm"
                  className="h-auto w-full justify-start rounded-xl px-2 py-1.5 font-normal shadow-none hover:bg-transparent"
                  onShared={({ snapshotId, shareToken }) => {
                    patchSidebarDeepResearchHistory(queryClient, item.drid, {
                      snapshot_id: snapshotId,
                      share_token: shareToken,
                    });
                  }}
                >
                  <>
                    <Share2Icon className="h-4 w-4" />
                    分享
                  </>
                </ShareButton>
              </div>
            </DropdownMenuItem>
            <DropdownMenuItem
              className="rounded-xl"
              disabled={!canShare}
              asChild
              onSelect={(event) => {
                event.preventDefault();
              }}
            >
              <div>
                <PublishSquareButton
                  disabled={!canShare}
                  sid={item.drid}
                  type="deepresearch"
                  snapshotId={item.snapshot_id}
                  isPublished={!!item.published_post}
                  publishedPost={item.published_post}
                  squareUrl={
                    item.published_post?.token
                      ? `/share/p/${item.published_post.token}`
                      : null
                  }
                  variant="ghost"
                  size="sm"
                  className="h-auto w-full justify-start rounded-xl px-2 py-1.5 font-normal shadow-none hover:bg-transparent"
                  onPublished={({ published, snapshotId }) => {
                    patchSidebarDeepResearchHistory(queryClient, item.drid, {
                      share_to_square: published,
                      snapshot_id: snapshotId,
                    });
                  }}
                >
                  <>
                    <SendIcon className="h-4 w-4" />
                    {item.published_post ? '更新发布' : '发布'}
                  </>
                </PublishSquareButton>
              </div>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        {item.status === 'ERROR' && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                className={`pointer-events-none absolute right-1 top-1/2 inline-flex h-7 w-7 -translate-y-1/2 items-center justify-center text-destructive transition-opacity group-hover/item:opacity-0 ${
                  active ? 'bg-sidebar-accent' : 'bg-sidebar'
                }`}
              >
                <AlertCircleIcon className="h-3.5 w-3.5" />
              </span>
            </TooltipTrigger>
            <TooltipContent>
              <p>{item.error_msg || '处理失败'}</p>
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    );
  });
}
